import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import Navigation from '../components/Navigation';
import { getSeriesBySlug, seriesList } from '../data/seriesData';

const Series = () => {
  const { slug } = useParams();
  const series = getSeriesBySlug(slug);
  const [selectedWork, setSelectedWork] = useState(null);

  if (!series) {
    return <Navigate to="/" replace />;
  }

  const nextSeries = seriesList.find(s => s.order === series.order + 1);
  const prevSeries = seriesList.find(s => s.order === series.order - 1);

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="series-page">
      <Navigation />

      {/* Header Section */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-neutral-900 via-neutral-800 to-stone-900">
        <div className="container mx-auto max-w-5xl">
          <Link
            to="/"
            data-testid="back-to-home-link"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-red-500 transition-colors mb-8 text-sm"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          <p className="text-red-500 font-medium mb-3 tracking-wide uppercase text-xs font-mono">
            Series {String(series.order).padStart(2, '0')} / {String(seriesList.length).padStart(2, '0')}
          </p>
          <h1
            data-testid="series-title"
            className="text-4xl md:text-6xl font-bold text-neutral-50 mb-6 leading-tight"
          >
            {series.title}
          </h1>
          <p
            data-testid="series-description"
            className="text-lg md:text-xl text-neutral-300 leading-relaxed max-w-3xl"
          >
            {series.description}
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 md:py-24 px-6">
        <div className="container mx-auto max-w-7xl">
          {series.works.length === 0 ? (
            <div
              data-testid="empty-series-message"
              className="text-center py-24 border-2 border-dashed border-neutral-300 rounded-lg"
            >
              <p className="text-neutral-500 text-lg mb-2">
                Works from this series will appear here soon.
              </p>
              <p className="text-neutral-400 text-sm">
                Images for "{series.title}" are coming.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between mb-12 flex-wrap gap-4">
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-900">
                  Works
                </h2>
                <p className="text-neutral-500 font-mono text-sm">
                  {series.works.length} {series.works.length === 1 ? 'piece' : 'pieces'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                {series.works.map((work, index) => (
                  <div
                    key={work.id}
                    data-testid={`work-card-${work.id}`}
                    className="group cursor-pointer"
                    onClick={() => setSelectedWork(work)}
                  >
                    <div className="aspect-square overflow-hidden bg-neutral-200 mb-4 shadow-lg group-hover:shadow-2xl transition-shadow duration-500">
                      <img
                        src={work.image}
                        alt={work.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex items-baseline gap-4">
                      <span className="text-neutral-400 font-mono text-sm">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-neutral-900 group-hover:text-red-600 transition-colors">
                          {work.title}
                        </h3>
                        <p className="text-sm text-neutral-500 mt-1">
                          {work.medium} • {work.year}
                        </p>
                        {work.dimensions && work.dimensions !== 'TBD' && (
                          <p className="text-sm text-neutral-500">{work.dimensions}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Series Navigation Footer */}
      <section className="border-t border-neutral-200 py-12 px-6 bg-neutral-100">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-2 gap-6">
            <div>
              {prevSeries ? (
                <Link
                  to={`/series/${prevSeries.slug}`}
                  data-testid="prev-series-link"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group block"
                >
                  <p className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
                    ← Previous Series
                  </p>
                  <p className="text-lg font-bold text-neutral-900 group-hover:text-red-600 transition-colors">
                    {prevSeries.title}
                  </p>
                </Link>
              ) : (
                <div />
              )}
            </div>
            <div className="text-right">
              {nextSeries ? (
                <Link
                  to={`/series/${nextSeries.slug}`}
                  data-testid="next-series-link"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group block"
                >
                  <p className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
                    Next Series →
                  </p>
                  <p className="text-lg font-bold text-neutral-900 group-hover:text-red-600 transition-colors">
                    {nextSeries.title}
                  </p>
                </Link>
              ) : (
                <div />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedWork && (
        <div
          data-testid="lightbox-modal"
          className="fixed inset-0 z-[60] bg-neutral-950/95 flex items-center justify-center p-4 md:p-8"
          onClick={() => setSelectedWork(null)}
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setSelectedWork(null)}
            className="absolute top-6 right-6 text-neutral-300 hover:text-red-500 transition-colors z-10"
            aria-label="Close"
          >
            <X size={32} />
          </button>
          <div
            className="max-w-6xl w-full max-h-[90vh] flex flex-col md:flex-row gap-6 items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-1 max-h-[80vh] overflow-hidden">
              <img
                src={selectedWork.image}
                alt={selectedWork.title}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="md:w-72 text-neutral-200">
              <p className="text-xs uppercase tracking-wider text-red-500 mb-2 font-mono">
                {series.title}
              </p>
              <h3 className="text-2xl font-bold mb-3 text-neutral-50">
                {selectedWork.title}
              </h3>
              <p className="text-neutral-400 mb-1">{selectedWork.medium}</p>
              <p className="text-neutral-400 mb-1">{selectedWork.year}</p>
              {selectedWork.dimensions && selectedWork.dimensions !== 'TBD' && (
                <p className="text-neutral-400">{selectedWork.dimensions}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Series;
